import React from 'react';
import { Atom, ShieldCheck, Database, CheckCircle, AlertTriangle, XCircle } from 'lucide-react';
import { Card } from '../components/ds/Card';

const SectionCard = ({ icon, title, children }) => (
  <Card className="p-8 bg-gradient-to-br from-surface to-surface/50 hover:border-primary/30 transition-colors">
    <div className="flex items-start gap-5">
      <div className="bg-primary/10 text-primary p-3 rounded-xl border border-primary/20">{icon}</div>
      <div className="flex-1">
        <h2 className="text-xl font-heading font-bold text-text-primary mb-4">{title}</h2>
        <div className="text-base text-text-secondary space-y-4 leading-relaxed">{children}</div>
      </div>
    </div>
  </Card>
);

const PlayabilityIndicator = ({ color, title, description }) => (
  <div className="flex items-start gap-4 p-4 bg-surface-light rounded-lg border border-border">
    <div className={`w-5 h-5 rounded-full ${color} flex-shrink-0 mt-0.5`}></div>
    <div>
      <div className="font-semibold text-text-primary text-base mb-1">{title}</div>
      <div className="text-sm text-text-secondary">{description}</div>
    </div>
  </div>
);

export default function AboutPage() {
  return (
    <div className="max-w-4xl mx-auto space-y-8 px-4 py-12">
      <div className="text-center mb-16">
        <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-text-primary mb-4 tracking-tight">
          The Science of PocketFitter
        </h1>
        <div className="w-20 h-1 bg-gradient-to-r from-primary to-primary-light mx-auto mb-6 rounded-full"></div>
        <p className="text-lg text-text-secondary max-w-2xl mx-auto leading-relaxed">
          We combine a validated physics engine with comprehensive equipment data to provide tour-level insights.
        </p>
      </div>

      <div className="space-y-8">
        <SectionCard icon={<Atom size={28} />} title="Trajectory Physics Engine">
          <p>
            PocketFitter's core is a sophisticated <strong className="text-primary">4-DOF (Degrees of Freedom) trajectory solver</strong>. Unlike simpler ballistic models, our engine simulates the forces that shape a golf ball's flight with high fidelity:
          </p>
          <ul className="space-y-3 pl-1">
            <li className="flex items-start gap-3">
              <span className="text-primary mt-1">•</span>
              <span><strong className="text-text-primary">Gravity:</strong> The constant downward force on the ball.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-primary mt-1">•</span>
              <span><strong className="text-text-primary">Drag:</strong> Air resistance that slows the ball down, calculated using a dynamic drag coefficient that changes with speed (the "drag crisis").</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-primary mt-1">•</span>
              <span><strong className="text-text-primary">Lift (Magnus Effect):</strong> The upward or downward force generated by the ball's backspin, crucial for determining carry distance and apex.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-primary mt-1">•</span>
              <span><strong className="text-text-primary">Wind:</strong> The model incorporates wind speed and direction, calculating its effect on the ball's velocity vector at each step of the simulation.</span>
            </li>
          </ul>
          <div className="pt-2 border-t border-border mt-4">
            <p>
              We use a <strong className="text-primary">Runge-Kutta 4th Order (RK4)</strong> numerical method to integrate these forces over time, providing a step-by-step simulation of the ball's entire flight path. This ensures our carry, total distance, and max height predictions are grounded in real-world physics.
            </p>
          </div>
        </SectionCard>

        <SectionCard icon={<ShieldCheck size={28} />} title="Dispersion & Playability">
          <p>
            Distance is only one part of the equation. "Playability" determines how well a shot will perform on the course. We quantify this with a <strong className="text-primary">Playability Score (0-100)</strong>, represented by a simple traffic light system.
          </p>
          <div className="space-y-3 pt-2">
            <PlayabilityIndicator
              color="bg-success"
              title="Good (> 80)"
              description="Optimal descent angle and spin. The ball will stop effectively on the green, providing control for approach shots."
            />
            <PlayabilityIndicator
              color="bg-warning"
              title="Moderate (51-80)"
              description="Marginal conditions. The ball may have extra roll-out on firm greens or is losing some distance to excessive spin."
            />
            <PlayabilityIndicator
              color="bg-error"
              title="Poor (< 50)"
              description="Indicates a significant issue, such as a descent angle that is too shallow (won't hold the green) or critically low spin (a 'knuckleball' that is hard to control)."
            />
          </div>
        </SectionCard>

        <SectionCard icon={<Database size={28} />} title="Data Sources">
          <p>
            Our recommendations are only as good as our data. We source our equipment specifications from a single, unified database that aggregates information from two primary channels:
          </p>
          <ul className="space-y-3 pl-1">
            <li className="flex items-start gap-3">
              <span className="text-primary mt-1">•</span>
              <span><strong className="text-text-primary">OEM Specifications:</strong> We directly integrate detailed equipment data provided by original equipment manufacturers (OEMs). This includes critical physics parameters like Center of Gravity (CG) location, Moment of Inertia (MOI), and spin/launch modifiers that are not publicly available.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-primary mt-1">•</span>
              <span><strong className="text-text-primary">Launch Monitor Validation:</strong> Our team conducts extensive testing using top-tier launch monitors (like TrackMan and Foresight GCQuad) to validate and supplement OEM data. This real-world testing ensures our simulations accurately reflect how clubs perform in a player's hands.</span>
            </li>
          </ul>
          <div className="pt-2 border-t border-border mt-4">
            <p>
              This hybrid approach gives us a robust and reliable dataset, making PocketFitter's recommendations a trustworthy source for your next equipment change.
            </p>
          </div>
        </SectionCard>
      </div>

      <div className="text-center pt-12 pb-4">
        <div className="inline-block px-6 py-3 bg-surface border border-border rounded-lg">
          <p className="text-sm text-text-muted">
            © 2026 PocketFitter. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
}
